import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SLOTS, fmt12Short } from '@/pages/med/medUtils';

// Two-char abbreviations shown inside each calendar cell, per owner's
// spec: 1M (Morning Meds), AB (Afternoon Buspar), NB (Night Time
// Buspar), 2M (Bedtime Meds).
const CAL_ABBR = {
  morning: '1M',
  afternoon: 'AB',
  night: 'NB',
  bedtime: '2M',
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Build the cell layout for a given year/month.
//
// - `cols`: 7 for the weekday-aligned desktop grid, 6 for the compact
//   phone grid (per owner spec: more room for the mini summary).
// - `includeLeading`: when true, pad the first row with trailing days
//   from the previous month so a weekday-aligned grid lines up under
//   Sun–Sat headers. Phone (6-col) uses `false` because 6 columns
//   can't match a 7-day week anyway.
//
// Trailing days from the next month are only added up to the end of
// the row that the last current-month day lands on. There is never a
// bottom row that is entirely next-month days.
function buildMonthGrid(year, month, cols, includeLeading) {
  const cells = [];

  if (includeLeading) {
    const first = new Date(year, month, 1);
    const startWeekday = first.getDay(); // 0 = Sun
    for (let i = startWeekday; i > 0; i -= 1) {
      const d = new Date(year, month, 1 - i);
      cells.push({ date: d, iso: ymd(d), inMonth: false });
    }
  }

  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDay; d += 1) {
    const date = new Date(year, month, d);
    cells.push({ date, iso: ymd(date), inMonth: true });
  }

  const remainder = cells.length % cols;
  if (remainder > 0) {
    const need = cols - remainder;
    for (let i = 1; i <= need; i += 1) {
      const d = new Date(year, month + 1, i);
      cells.push({ date: d, iso: ymd(d), inMonth: false });
    }
  }

  return cells;
}

/**
 * Calendar View for the Medicine tracker.
 *
 * Props:
 *   year, month           – currently displayed month (month is 0-indexed)
 *   onChangeMonth(y, m)   – prev/next month navigation
 *   fullyTakenSet         – Set<string> of ISO dates ('YYYY-MM-DD') that were fully taken
 *   daysByIso             – Object<string, dayDoc> keyed by ISO date, used to
 *                           draw the mini summary inside each cell
 *   onSelectDay(iso)      – called when the user clicks an editable day (today or past)
 *   onOpenNotes(iso)      – called when the user clicks the "Notes" link on a cell
 */
export default function MedCalendar({
  year,
  month,
  onChangeMonth,
  fullyTakenSet,
  daysByIso,
  onSelectDay,
  onOpenNotes,
  todayIso: todayIsoProp,
}) {
  // "Today" for calendar coloring must use the MedTrack logical day
  // (4 AM rollover) so a page loaded at 2 AM Tuesday still highlights
  // Monday as yellow and doesn't flag Monday as "missed" prematurely.
  // The parent passes it in; fall back to system-day if omitted (kept
  // as a defensive default in case this component is reused).
  const todayIso = useMemo(() => {
    if (todayIsoProp && /^\d{4}-\d{2}-\d{2}$/.test(todayIsoProp)) return todayIsoProp;
    return ymd(startOfDay(new Date()));
  }, [todayIsoProp]);
  const today = useMemo(() => {
    const [y, m, d] = todayIso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [todayIso]);
  // Two layouts: 7-col weekday-aligned for desktop, 6-col compact for
  // phone (more room per cell for the mini summary).
  const desktopCells = useMemo(() => buildMonthGrid(year, month, 7, true), [year, month]);
  const mobileCells = useMemo(() => buildMonthGrid(year, month, 6, false), [year, month]);

  const goPrev = () => {
    const d = new Date(year, month - 1, 1);
    onChangeMonth(d.getFullYear(), d.getMonth());
  };
  const goNext = () => {
    const d = new Date(year, month + 1, 1);
    onChangeMonth(d.getFullYear(), d.getMonth());
  };
  const goToday = () => onChangeMonth(today.getFullYear(), today.getMonth());

  return (
    <div className="w-full max-w-3xl mx-auto" data-testid="med-calendar">
      {/* Month header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={goPrev}
          className="p-2 rounded-md hover:bg-slate-100 text-slate-700"
          aria-label="Previous month"
          data-testid="med-calendar-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <h2
            className="text-xl font-semibold text-slate-900"
            data-testid="med-calendar-title"
          >
            {MONTHS[month]} {year}
          </h2>
          <button
            type="button"
            onClick={goToday}
            className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="med-calendar-today-btn"
          >
            Today
          </button>
        </div>
        <button
          type="button"
          onClick={goNext}
          className="p-2 rounded-md hover:bg-slate-100 text-slate-700"
          aria-label="Next month"
          data-testid="med-calendar-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Weekday header — desktop only (6-col mobile grid isn't
          weekday-aligned so headers wouldn't match). */}
      <div className="hidden sm:grid grid-cols-7 gap-1.5 mb-1.5">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-center"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Desktop grid (7 columns, weekday-aligned) */}
      <div className="hidden sm:grid grid-cols-7 gap-1.5" data-testid="med-calendar-grid-desktop">
        {desktopCells.map((cell) => renderCell(cell, {
          today, todayIso, fullyTakenSet, daysByIso, onSelectDay, onOpenNotes,
        }))}
      </div>

      {/* Mobile grid (6 columns, days-only flow) */}
      <div className="grid sm:hidden grid-cols-6 gap-1" data-testid="med-calendar-grid-mobile">
        {mobileCells.map((cell) => renderCell(cell, {
          today, todayIso, fullyTakenSet, daysByIso, onSelectDay, onOpenNotes,
        }))}
      </div>

      {/* Legend */}
      <div
        className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600"
        data-testid="med-calendar-legend"
      >
        <LegendSwatch borderClass="border-emerald-500" fillClass="bg-emerald-50" label="Fully taken" />
        <LegendSwatch borderClass="border-yellow-400" fillClass="bg-yellow-50" label="Today" />
        <LegendSwatch borderClass="border-rose-400" fillClass="bg-rose-50" label="Missed" />
        <LegendSwatch borderClass="border-slate-300" fillClass="bg-slate-100" label="Upcoming" />
      </div>
    </div>
  );
}

function LegendSwatch({ borderClass, fillClass, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-block w-4 h-4 rounded-sm border-2 ${borderClass} ${fillClass}`}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

// Shared cell renderer used by both the desktop (7-col) and mobile
// (6-col) grids so their contents stay pixel-identical apart from
// the outer layout.
function renderCell(cell, { today, todayIso, fullyTakenSet, daysByIso, onSelectDay, onOpenNotes }) {
  const isToday = cell.iso === todayIso;
  const isFuture = cell.date > today;
  const isFullyTaken = fullyTakenSet?.has(cell.iso);
  const isEditable = !isFuture; // today + past
  // "Missed" = a past day (strictly before today) that wasn't fully
  // taken. Today never counts as missed — the day isn't over.
  const isMissed = !isFuture && !isToday && !isFullyTaken;

  let stateClasses = 'bg-white border-slate-200 text-slate-800';
  if (isMissed) {
    // Past + not fully taken → rose-pink outline, faint rose interior.
    stateClasses = 'bg-rose-50 border-rose-400 text-slate-800';
  }
  if (isFullyTaken) {
    stateClasses = 'bg-emerald-50 border-emerald-500 text-emerald-900';
  }
  if (isFuture) {
    stateClasses = 'bg-slate-100 border-slate-300 text-slate-500';
  }
  if (isToday) {
    stateClasses = 'bg-yellow-50 border-yellow-400 text-slate-900';
  }
  if (!cell.inMonth) {
    stateClasses += ' opacity-45';
  }

  const clickable = isEditable && cell.inMonth;
  const dayData = daysByIso?.[cell.iso];
  const hasAnyCheck = !!dayData && (
    !!dayData.morning?.checked ||
    !!dayData.afternoon?.checked ||
    !!dayData.night?.checked ||
    !!dayData.bedtime?.checked
  );
  const hasNote = !!(dayData && typeof dayData.note === 'string' && dayData.note.trim().length > 0);

  const handleActivate = clickable ? () => onSelectDay(cell.iso) : undefined;

  return (
    <div
      key={cell.iso}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? handleActivate : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      } : undefined}
      className={[
        'min-h-[72px] sm:min-h-[110px] rounded-md border-2 flex flex-col items-stretch p-1 sm:p-1.5',
        'text-left transition-colors overflow-hidden',
        stateClasses,
        clickable ? 'cursor-pointer hover:brightness-95' : 'cursor-default',
        isToday ? 'ring-2 ring-yellow-200/60' : '',
      ].join(' ')}
      data-testid={`med-calendar-day-${cell.iso}`}
      data-today={isToday ? '1' : '0'}
      data-fully-taken={isFullyTaken ? '1' : '0'}
      data-missed={isMissed ? '1' : '0'}
      data-future={isFuture ? '1' : '0'}
      aria-label={`${cell.iso}${isToday ? ' (today)' : ''}${isFullyTaken ? ' fully taken' : ''}${isMissed ? ' missed' : ''}${isFuture ? ' upcoming' : ''}`}
    >
      {/* Top row: date number (left) + Notes link (right, all shown days) */}
      <div className="flex items-start justify-between gap-1">
        <span className="text-sm font-semibold leading-none">
          {cell.date.getDate()}
        </span>
        {onOpenNotes && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenNotes(cell.iso);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className="flex items-center gap-[3px] text-[9px] sm:text-[10px] leading-none text-slate-600 hover:text-slate-900 hover:underline"
            data-testid={`med-calendar-notes-${cell.iso}`}
            data-has-note={hasNote ? '1' : '0'}
            aria-label={hasNote ? `Edit notes for ${cell.iso}` : `Add notes for ${cell.iso}`}
          >
            <span className="hidden sm:inline">Notes</span>
            <NoteCircle filled={hasNote} />
          </button>
        )}
      </div>

      {cell.inMonth && hasAnyCheck && (
        <div
          className="mt-2 space-y-[1px] text-[9px] sm:text-[10px] leading-tight"
          data-testid={`med-calendar-summary-${cell.iso}`}
        >
          {SLOTS.map((s) => {
            const slot = dayData[s.key] || {};
            const checked = !!slot.checked;
            return (
              <div
                key={s.key}
                className="grid grid-cols-[auto_1fr] gap-[3px] items-center min-w-0"
                data-testid={`med-calendar-cell-${cell.iso}-${s.key}`}
                data-checked={checked ? '1' : '0'}
              >
                <div className="flex items-center gap-[2px] min-w-0">
                  <MiniCheck checked={checked} />
                  <span className="font-semibold text-slate-800">
                    {CAL_ABBR[s.key]}
                  </span>
                </div>
                <div className="text-right tabular-nums text-slate-700 truncate">
                  {checked && slot.taken_time ? fmt12Short(slot.taken_time) : '–'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Tiny checkbox glyph used inside each calendar cell. Sized to fit the
// summary rows without wrapping the abbreviation.
function MiniCheck({ checked }) {
  if (checked) {
    return (
      <span
        className="inline-flex w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-[2px] bg-emerald-500 items-center justify-center text-white flex-shrink-0"
        aria-hidden="true"
      >
        <svg viewBox="0 0 10 10" className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M2 5 L4.5 7.5 L8 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className="inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-[2px] border border-slate-400 bg-white flex-shrink-0"
      aria-hidden="true"
    />
  );
}

// Lime-green circle next to the "Notes" link. Outline when the day
// has no note saved, filled when it does.
function NoteCircle({ filled }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'inline-block w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0',
        filled ? 'bg-lime-500' : 'border-2 border-lime-500 bg-white',
      ].join(' ')}
    />
  );
}
